import { traceLevel } from '../core/level';
import type { Level } from '../core/types';
import { store } from '../game/store';
import { BoardRenderer } from '../render/board';
import { CHAPTERS, LEVELS } from '../levels';
import { type Screen, app, markReady } from './app';
import { confirmDialog, openSettings } from './dialogs';
import { ICONS, h, icon } from './dom';
import { nextLevelId } from './title';

export function starIcons(n: number, max = 3, cls = 'stars'): HTMLElement {
  const wrap = h('span', { class: cls, role: 'img', 'aria-label': `${n} of ${max} stars` });
  for (let i = 0; i < max; i++) {
    const s = icon(ICONS.star);
    if (i < n) s.classList.add('on');
    wrap.append(s);
  }
  return wrap;
}

/** Draw a level preview (empty board, so you see the puzzle) into a canvas. */
export function drawThumbnail(canvas: HTMLCanvasElement, level: Level, cssW: number) {
  const cssH = Math.round((cssW * 2) / 3);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const r = new BoardRenderer(canvas);
  r.resize(cssW, cssH, dpr);
  canvas.style.width = '';
  canvas.style.height = '';
  r.setSlot({ x: 0, y: 0, w: cssW, h: cssH }, false);
  r.setLevel(level);
  r.render(
    {
      level,
      placed: [],
      trace: traceLevel(level, []),
      drag: null,
      focus: null,
      hints: [],
      litAt: [],
    },
    {
      time: 0,
      reduced: true,
      patterns: store.settings.patterns,
      contrast: store.settings.contrast,
    },
  );
}

export function levelsScreen(): Screen {
  let cancelled = false;
  return {
    title: 'Levels',
    scroll: true,
    mount(root) {
      cancelled = false;
      const nextId = nextLevelId();
      const totalStars = Object.values(store.progress.levels).reduce((s, r) => s + r.stars, 0);
      const solved = Object.keys(store.progress.levels).filter((id) =>
        LEVELS.some((l) => l.id === id),
      ).length;
      const thumbs: { canvas: HTMLCanvasElement; level: Level }[] = [];

      const chapters = CHAPTERS.map((ch) => {
        const levels = LEVELS.filter((l) => l.id.startsWith(`${ch.n}-`));
        const chStars = levels.reduce((s, l) => s + (store.progress.levels[l.id]?.stars ?? 0), 0);
        return h(
          'section',
          { class: 'chapter', 'aria-labelledby': `ch-${ch.n}` },
          h(
            'div',
            { class: 'chapter-head' },
            h('h3', { id: `ch-${ch.n}`, text: `${ch.numeral}. ${ch.title}` }),
            h('p', { text: ch.blurb }),
            h('span', { class: 'label', text: `${chStars} / ${levels.length * 3} stars` }),
          ),
          h(
            'ul',
            { class: 'level-grid' },
            ...levels.map((l, i) => {
              const rec = store.progress.levels[l.id];
              const canvas = h('canvas', { width: '300', height: '200', 'aria-hidden': 'true' });
              thumbs.push({ canvas, level: l });
              const isNext = l.id === nextId;
              return h(
                'li',
                {},
                h(
                  'a',
                  {
                    class: `level-card${isNext ? ' next' : ''}`,
                    href: `#/play/${l.id}`,
                    'aria-label': `Level ${ch.numeral}.${i + 1}, ${l.name}. ${rec ? `${rec.stars} of 3 stars` : 'not solved yet'}${isNext ? '. Next up' : ''}`,
                  },
                  canvas,
                  isNext ? h('span', { class: 'badge', text: 'Next' }) : null,
                  h(
                    'span',
                    { class: 'meta' },
                    h('span', { class: 'num', text: String(i + 1) }),
                    h('span', { class: 'name', text: l.name }),
                    starIcons(rec?.stars ?? 0),
                  ),
                ),
              );
            }),
          ),
        );
      });

      const resetBtn = h(
        'button',
        { class: 'btn ghost', type: 'button' },
        icon(ICONS.reset),
        'Reset progress',
      );
      resetBtn.addEventListener('click', async () => {
        const ok = await confirmDialog(
          'Reset progress?',
          'This clears every star and endless record on this device. It cannot be undone.',
          'Reset',
        );
        if (ok) {
          store.resetProgress();
          app.navigate('#/levels');
        }
      });

      const el = h(
        'div',
        { class: 'levels' },
        h(
          'header',
          { class: 'topbar' },
          h(
            'a',
            { class: 'icon-btn', href: '#/', 'aria-label': 'Back to title' },
            icon(ICONS.back),
          ),
          h('h1', { class: 'wordmark grow', text: 'Lensing' }),
          h(
            'button',
            {
              class: 'icon-btn',
              type: 'button',
              'aria-label': 'Settings',
              onclick: () => openSettings(),
            },
            icon(ICONS.gear),
          ),
        ),
        h('h2', { text: 'Choose a plate' }),
        h(
          'p',
          { class: 'levels-summary' },
          h('span', { html: `<strong>${solved}</strong> of ${LEVELS.length} solved` }),
          h('span', { html: `<strong>${totalStars}</strong> of ${LEVELS.length * 3} stars` }),
        ),
        ...chapters,
        h(
          'p',
          { class: 'levels-summary', style: { marginTop: '34px' } },
          h('a', { href: '#/endless', text: 'Endless puzzles' }),
          h('a', { href: '#/editor', text: 'Level editor' }),
          h('a', {
            href: 'https://mustafaobaidd1.github.io/',
            text: '← Mustafa Obaid · Portfolio',
          }),
          h('a', { href: 'https://github.com/mustafaobaidd1/lensing-game', text: 'Source code' }),
          resetBtn,
        ),
      );
      root.append(el);
      markReady();

      // Draw thumbnails a few at a time so the page stays responsive.
      let i = 0;
      const batch = () => {
        if (cancelled) return;
        const end = Math.min(thumbs.length, i + 4);
        for (; i < end; i++) drawThumbnail(thumbs[i]!.canvas, thumbs[i]!.level, 300);
        if (i < thumbs.length) window.setTimeout(batch, 16);
      };
      window.setTimeout(batch, 0);
      if (nextId && solved > 0) {
        const card = el.querySelector('.level-card.next');
        card?.scrollIntoView?.({ block: 'center' });
      }
    },
    unmount() {
      cancelled = true;
    },
    frame() {
      app.renderer.renderPaper(store.settings.contrast);
      return false;
    },
  };
}
